import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { ToastContainer, type ToastItem, type ToastType } from '@/components/ui/Toast';

interface ToastContextValue {
  /**
   * 功能：顯示一則視窗化通知（約 0.8 秒自動消失，0.5 秒先隱藏確定按鈕）
   * @param {string} message 通知內容
   * @param {ToastType} [type='error'] 通知類型
   * @returns {void}
   */
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let _nextId = 0;

/**
 * 功能：提供全域 Toast 狀態與視窗化通知 UI
 * @param {{ children: ReactNode }} props React 子節點
 * @returns {JSX.Element} Provider
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  /**
   * 功能：關閉指定 id 的通知
   * @param {number} id Toast id
   * @returns {void}
   */
  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * 功能：新增一則視窗通知（自動消失由 ToastDialog 內部計時）
   * @param {string} message 訊息文字
   * @param {ToastType} [type='error'] 類型
   * @returns {void}
   */
  const showToast = useCallback(
    (message: string, type: ToastType = 'error') => {
      const id = ++_nextId;

      if (process.env.NODE_ENV !== 'production') {
        const prefix = `[Toast][${type.toUpperCase()}]`;
        if (type === 'error') {
          console.error(prefix, message);
        } else {
          console.log(prefix, message);
        }
      }

      // 視窗化通知同時間只保留最新一則，避免堆疊遮罩
      setToasts([{ id, message, type }]);
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/**
 * 功能：取得 showToast（視窗通知約 0.8 秒自動關閉，0.5 秒先隱藏確定）
 * @returns {ToastContextValue} Toast API
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
